'use client'

import { useEffect, useState } from 'react'
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from 'framer-motion'
import { CreditCard, LayoutTemplate, MailCheck, UserPlus } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen } from './demoHooks'

// SaaS CRM launch: a revenue planner for GoHighLevel SaaS mode. The visitor
// picks a plan price, a client count and a usage markup; the monthly figure
// counts to the new value, and the strip underneath shows what SaaS mode does
// automatically for every signup. Plain maths on the visitor's own inputs,
// labelled as an example and before GoHighLevel's own fees.

const PRICES = [97, 297, 497]
const MARKUPS = [1.2, 1.4, 2]
const STEPS = [
  { Icon: UserPlus, text: 'Client signs up' },
  { Icon: CreditCard, text: 'Stripe charges the card' },
  { Icon: LayoutTemplate, text: 'Account built from your snapshot' },
  { Icon: MailCheck, text: 'Welcome email sent' },
]

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`

/** A number that counts to its new value instead of jumping. */
function CountUp({ value, className }) {
  const reduce = useReducedMotion()
  const mv = useMotionValue(value)
  const text = useTransform(mv, (v) => money(v))
  useEffect(() => {
    if (reduce) { mv.set(value); return undefined }
    const controls = animate(mv, value, { duration: 0.7, ease: [0.22, 1, 0.36, 1] })
    return () => controls.stop()
  }, [value, mv, reduce])
  return <motion.span className={className}>{text}</motion.span>
}

export default function SaasPlanner() {
  const [ref, onScreen] = useOnScreen()
  const [price, setPrice] = useState(297)
  const [clients, setClients] = useState(40)
  const [markup, setMarkup] = useState(1.4)
  const [active, setActive] = useState(0)
  const reduce = useReducedMotion()

  // The provisioning strip loops while the demo is on screen.
  useEffect(() => {
    if (!onScreen || reduce) return undefined
    const timer = setTimeout(() => setActive((i) => (i + 1) % (STEPS.length + 1)), active === STEPS.length ? 1600 : 1100)
    return () => clearTimeout(timer)
  }, [onScreen, reduce, active])

  const monthly = onScreen ? clients * price : 0
  const keepCents = Math.round((markup - 1) * 100)

  return (
    <DemoFrame frameRef={ref} className="svd-saas" title="Your SaaS revenue planner" label="Demo: plan your GoHighLevel SaaS mode revenue">
      <div className="svd-saas-figure" aria-live="polite">
        <span className="svd-saas-label">Monthly subscription revenue</span>
        <span className="svd-3d-wrap">
          <CountUp value={monthly} className="svd-3d-num" />
          <span className="svd-3d-unit">/mo</span>
        </span>
        <span className="svd-saas-sub">
          <CountUp value={monthly * 12} /> a year from {clients} clients on the {money(price)} plan
        </span>
      </div>

      <div className="svd-controls">
        <div className="svd-control">
          <span className="svd-control-label" id="svd-price-label">Plan price</span>
          <div className="svd-seg" role="group" aria-labelledby="svd-price-label">
            {PRICES.map((p) => (
              <button key={p} type="button" aria-pressed={price === p} className={price === p ? 'is-on' : ''} onClick={() => setPrice(p)}>
                {money(p)}
              </button>
            ))}
          </div>
        </div>
        <label className="svd-control">
          <span className="svd-control-label">Clients <b>{clients}</b></span>
          <input
            type="range"
            min="5"
            max="250"
            step="5"
            value={clients}
            onChange={(e) => setClients(Number(e.target.value))}
            className="svd-range"
            style={{ '--fill': `${((clients - 5) / 245) * 100}%` }}
          />
        </label>
        <div className="svd-control">
          <span className="svd-control-label" id="svd-markup-label">Usage markup</span>
          <div className="svd-seg" role="group" aria-labelledby="svd-markup-label">
            {MARKUPS.map((m) => (
              <button key={m} type="button" aria-pressed={markup === m} className={markup === m ? 'is-on' : ''} onClick={() => setMarkup(m)}>
                {m}×
              </button>
            ))}
          </div>
        </div>
        <p className="svd-markup-note">
          At {markup}×, you keep <b>{keepCents}¢</b> of every $1 your clients spend on calls, texts, email and AI.
        </p>
      </div>

      <ol className="svd-provision" aria-label="What SaaS mode does for every new client">
        {STEPS.map((s, i) => (
          <li key={s.text} className={active > i ? 'is-done' : active === i ? 'is-current' : ''}>
            <span className="svd-provision-icon" aria-hidden="true"><s.Icon size={16} /></span>
            <span>{s.text}</span>
          </li>
        ))}
      </ol>
      <p className="svd-footnote">Example maths before GoHighLevel&apos;s own fees.</p>
    </DemoFrame>
  )
}
